import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';
import { formatApiDateTime } from '../admin/dateTimeUtils';

type KnowledgeDocument = {
    documentId: string;
    fileName: string;
    contentType: string;
    fileSizeBytes: number;
    searchStatus: string;
    uploadedAt: string;
};

function apiRoot(): { url: string; token: string } {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const base = String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1').replace(/\/+$/, '');
    const version = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    return { url: /\/api\/v[^/]+$/i.test(base) ? base : `${base}/api/v${version}`, token: String(session?.adminToken ?? session?.accessToken ?? '') };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const config = apiRoot();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (config.token) headers.set('Authorization', `Bearer ${config.token}`);
    const response = await fetch(`${config.url}/AdminUltronKnowledge${path}`, { ...init, headers });
    const payload = await response.json();
    if (!response.ok || payload?.success === false) throw new Error(payload?.errorMessage ?? 'Knowledge document request failed');
    return payload.data as T;
}

const formatFileSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(bytes < 1024 * 1024 ? 2 : 1)} MB`;

export function UltronKnowledgePage() {
    const notify = useNotify();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const data = await request<{ items: KnowledgeDocument[] }>('', { method: 'GET' });
            setDocuments(data.items ?? []);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Unable to load knowledge documents', { type: 'error' });
        } finally { setBusy(false); }
    }, [notify]);
    useEffect(() => { void load(); }, [load]);
    const choose = (event: ChangeEvent<HTMLInputElement>) => setFile(event.target.files?.[0] ?? null);
    const upload = async () => {
        if (!file) return notify('Choose a PDF, Word, text, or Markdown document', { type: 'warning' });
        const form = new FormData(); form.set('file', file); setBusy(true);
        try {
            await request('', { method: 'POST', body: form });
            notify('Document uploaded and queued for AI search indexing', { type: 'info' });
            setFile(null); if (inputRef.current) inputRef.current.value = ''; await load();
        } catch (error) { notify(error instanceof Error ? error.message : 'Upload failed', { type: 'error' }); }
        finally { setBusy(false); }
    };
    const remove = async (document: KnowledgeDocument) => {
        if (!window.confirm(`Delete "${document.fileName}" from R2 and AI search?`)) return;
        setBusy(true);
        try { await request(`/${document.documentId}`, { method: 'DELETE' }); notify('Document deleted', { type: 'info' }); await load(); }
        catch (error) { notify(error instanceof Error ? error.message : 'Delete failed', { type: 'error' }); }
        finally { setBusy(false); }
    };
    return <Box><Title title="Ultron Knowledge" />{busy ? <LinearProgress /> : null}<Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box><Typography variant="h4">Ultron Knowledge</Typography><Typography color="text.secondary">Approved documents only. Each upload is retained in R2 and indexed for Ultron’s general PTE answers.</Typography></Box>
        <Paper variant="outlined" sx={{ p: 2.5 }}><Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Button component="label" variant="outlined">{file?.name ?? 'Choose document'}<input ref={inputRef} hidden type="file" accept="application/pdf,.pdf,.docx,text/plain,.txt,text/markdown,.md" onChange={choose} /></Button>
            <Button variant="contained" startIcon={<UploadFileRoundedIcon />} onClick={() => void upload()} disabled={busy}>Upload and index</Button>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => void load()} disabled={busy}>Refresh</Button>
        </Stack></Paper>
        {documents.length === 0 ? <Alert severity="info">No approved knowledge documents yet.</Alert> : <Stack spacing={1.5}>{documents.map((document) => <Paper key={document.documentId} variant="outlined" sx={{ p: 2 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}><Box sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{document.fileName}</Typography><Typography variant="body2" color="text.secondary">{formatFileSize(document.fileSizeBytes)} · Uploaded {formatApiDateTime(document.uploadedAt)}</Typography></Box><Chip size="small" label={`Index: ${document.searchStatus}`} /><Button color="error" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />} disabled={busy} onClick={() => void remove(document)}>Delete</Button></Stack></Paper>)}</Stack>}
    </Stack></Box>;
}
